import React, { useState, useEffect } from "react";
import "./LeaveRequestsManager.css";
import { getRole } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient"; // ✅ Use centralized API handler
import SessionHandler from "../components/SessionHandler"; // ✅ Global session expiry modal

const LeaveRequestsManager = () => {
  const [requests, setRequests] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(""); // approve/reject

  useEffect(() => {
    const role = getRole();
    setUserRole(role);

    if (role === "manager" || role === "admin") {
      fetchLeaveRequests(role);
    }
  }, []);

  const fetchLeaveRequests = async (role) => {
    try {
      const data = await apiClient("/leaves/");
      if (!data) return; // Session expired — handled by SessionHandler

      const leaveList = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      let filtered = leaveList.filter((req) => req.status === "Pending");

      if (role === "manager") {
        filtered = filtered.filter((req) => req.employee_role !== "manager");
      }

      setRequests(filtered);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    }
  };

  const openPopup = (req, action) => {
    setSelectedRequest(req);
    setActionType(action);
    setShowPopup(true);
  };

  const handleConfirm = async () => {
    if (!selectedRequest || !actionType) return;

    try {
      const url = `/leaves/${selectedRequest.id}/${actionType}/`;
      const result = await apiClient(url, { method: "POST" });
      if (!result) return; // handled globally

      // Remove processed request from list
      setRequests((prev) => prev.filter((req) => req.id !== selectedRequest.id));
      setShowPopup(false);
      setSelectedRequest(null);
      setActionType("");
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error);
    }
  };

  const handleCancel = () => {
    setShowPopup(false);
    setSelectedRequest(null);
    setActionType("");
  };

  if (userRole !== "manager" && userRole !== "admin") {
    return (
      <div className="leave-dashboard-mgr">
        <h2 className="leave-title-mgr">Access Denied</h2>
        <p className="leave-message-mgr">
          You are not authorized to view leave requests.
        </p>
        <SessionHandler /> {/* ✅ Keep modal active globally */}
      </div>
    );
  }

  return (
    <div className="leave-dashboard-mgr">
      <h2 className="leave-title-mgr">Pending Leave Requests</h2>
      {requests.length === 0 ? (
        <p className="leave-message-mgr">No pending requests.</p>
      ) : (
        <table className="leave-table-mgr">
          <thead>
            <tr>
              <th className="leave-th-mgr">Name</th>
              <th className="leave-th-mgr">Department</th>
              <th className="leave-th-mgr">Subject</th>
              <th className="leave-th-mgr">Start Date</th>
              <th className="leave-th-mgr">End Date</th>
              <th className="leave-th-mgr">Leave Type</th>
              <th className="leave-th-mgr">Days</th>
              <th className="leave-th-mgr">Status</th>
              <th className="leave-th-mgr">Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.id} className="leave-row-mgr">
                <td className="leave-td-mgr">{req.employee_name}</td>
                <td className="leave-td-mgr">{req.employee_department}</td>
                <td className="leave-td-mgr">{req.subject}</td>
                <td className="leave-td-mgr">{req.start_date}</td>
                <td className="leave-td-mgr">{req.end_date}</td>
                <td className="leave-td-mgr">{req.leave_type}</td>
                <td className="leave-td-mgr">{req.days}</td>
                <td className="leave-td-mgr">{req.status}</td>
                <td className="leave-td-mgr">
                  <button
                    className="btn-approve-mgr"
                    onClick={() => openPopup(req, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    className="btn-reject-mgr"
                    onClick={() => openPopup(req, "reject")}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Popup modal */}
      {showPopup && (
        <div className="modal13-overlay">
          <div className="modal13-content">
            <h3>
              Confirm {actionType === "approve" ? "Approval" : "Rejection"}
            </h3>
            <p>
              Are you sure you want to <b>{actionType}</b> the leave request for{" "}
              <b>{selectedRequest?.employee_name}</b>?
            </p>
            <div className="modal13-buttons">
              <button className="btn13-confirm" onClick={handleConfirm}>
                Yes, {actionType}
              </button>
              <button className="btn13-cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Global Session Expiry Modal */}
      <SessionHandler />
    </div>
  );
};

export default LeaveRequestsManager;
